'use client'

import { sendGenericEvent } from 'analytics/events/generics/sendGenericEvent'
import { useFieldValidations } from 'country-configuration/services/client/useFieldValidations'
import { FormProvider, useForm } from 'form-components'
import { EmailInput } from 'form-components/components/EmailInput/EmailInput'
import { SubmitButton } from 'form-components/components/SubmitButton/SubmitButton'
import { MODAL_TYPE, Modal, type ModalAPI } from 'fukku/Modal'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import dynamic from 'next/dynamic'
import { SnackbarStatus } from 'notifications/constants/constants'
import { useNotificationsContext } from 'notifications/hooks/useNotificationsContext/useNotificationsContext'
import { useRef } from 'react'

import { CommonGenericEvents } from '../../constants/analytics'
import { useSendRecoveryPassword } from '../../hooks/useSendRecoveryPassword'

import styles from './RecoveryPasswordForm.module.scss'

const ModalContent = dynamic(() =>
	import('../ModalContent/ModalContent').then((mod) => mod.ModalContent)
)

export function RecoveryPasswordForm() {
	const { sendEmailRecoveryPassword } = useSendRecoveryPassword()
	const modalRef = useRef<ModalAPI>(null)
	const { addNotification } = useNotificationsContext()

	const { t } = useLabels()

	const defaultEmailValue = {
		email: '',
	}
	const formMethods = useForm({
		defaultValues: defaultEmailValue,
		mode: 'onTouched',
	})
	const { handleSubmit } = formMethods
	const { email } = useFieldValidations()

	const closeModal = (): void => {
		modalRef.current?.close()
	}

	const onSubmit = async (values: { email: string }): Promise<void> => {
		const response = await sendEmailRecoveryPassword(values.email)
		if (response.success) {
			modalRef.current?.show()
			sendGenericEvent(CommonGenericEvents.REMEMBER_PASSWORD_SEND)
		} else {
			addNotification({
				id: 'recoveryPassword.snackbar.errorRecoveryPassword',
				status: SnackbarStatus.ERROR,
				title: t('login.rememberPassword.error.text'),
			})
		}
	}

	return (
		<FormProvider {...formMethods}>
			<form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
				<EmailInput
					name='email'
					id='recovery-password-email-input'
					rules={email}
					data-testid='recovery-password.emailInput.text'
					className={styles.email}
				/>
				<SubmitButton
					id='recovery-password-button'
					className={styles.submitButton}
					data-testid='recovery-password.recoveryPassword.button'
				>
					{t('login.send.button')}
				</SubmitButton>
			</form>
			<Modal
				type={MODAL_TYPE.S}
				ref={modalRef}
				showTitle={false}
				title={t('login.rememberPassword.mailSent.title')}
				name='recoveryPasswordForm.modal'
				data-testid='recovery-password.modal'
			>
				<ModalContent closeModal={closeModal} />
			</Modal>
		</FormProvider>
	)
}
